import React, { useContext, useState } from "react";
import {
  Box,
  Checkbox,
  ClickAwayListener,
  Dialog,
  Fade,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Paper,
  Popper,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";
import { overlayIndex, textColor, useStyles } from "./FilterStyle";
import "./Filter.scss";
import TableContext from "../TableContext";
import {
  IFilterOption,
  FilterColumnTypes,
  IStatus,
  IAdr,
} from "../Utils/Interfaces/IFilter";
import VesselSelect from "./Vessel/VesselSelect";
import AutocompleteTextbox from "../../AutoCompleteTextBox/AutocompleteTextbox";
import customColors from "../../../styles/coreStyles/ColorPalette";

const FilterOptions = ({
  filterColumnTypes,
}: {
  filterColumnTypes: FilterColumnTypes;
}) => {
  const [anchorEl, setAnchorEl] = useState<
    HTMLElement | (() => EventTarget) | null | undefined
  >(null);
  const [open, setOpen] = useState(false);
  const tableContextState = useContext(TableContext);
  const tableContext = tableContextState?.tableContext;
  const filters = tableContext ? tableContext.filters : null;

  const [isSelectOpen, setSelectOpen] = useState(false);

  const isStatusChecked = (status: IStatus): boolean => {
    if (filters === null) return false;
    const filteredFilter = filters?.filter((filter) => filter.name === status);
    if (filteredFilter && filteredFilter?.length === 0) return false;
    return filters!.filter((filter) => filter.name === status)[0]
      .value as boolean;
  };

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const { target } = event;
    setAnchorEl(target as HTMLElement);
    setOpen(!open);
  };

  const updateFilters = (
    existingFilters: IFilterOption[] | null,
    newFilter: IFilterOption
  ): IFilterOption[] => {
    const updatedFilters =
      existingFilters && existingFilters.length > 0
        ? existingFilters.filter((filter) => filter.name !== newFilter.name)
        : [];

    updatedFilters.push(newFilter);

    return updatedFilters;
  };

  const statusHandleChange =
    (name: IStatus) => (event: { target: { checked: unknown } }) => {
      const checked: boolean = event.target.checked as boolean;
      const filterOption: IFilterOption = {
        column: FilterColumnTypes.STATUS,
        label: name,
        name: name,
        value: checked,
      };

      const updatedFilters: IFilterOption[] = updateFilters(
        filters,
        filterOption
      );

      tableContextState?.updateFilters(updatedFilters);
    };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClickAway = () => {
    if (!isSelectOpen) setOpen(false);
  };

  const classes = useStyles();
  const id = open ? "simple-popper" : undefined;

  const isADRChecked = (label: IAdr): boolean => {
    return filters?.some((filter) => filter.label === label) ?? false;
  };

  const ADRhandleChange =
    (label: IAdr) => (event: { target: { checked: boolean } }) => {
      const checked = event.target.checked;
      let updatedFilters: IFilterOption[] = [];

      if (checked) {
        updatedFilters = [
          ...(filters?.filter(
            (filter) => filter.column !== FilterColumnTypes.ADR
          ) || []),
          {
            column: FilterColumnTypes.ADR,
            label: label,
            name: "ADR",
            value: label === IAdr.YES,
            showChipEvenFalse: true,
          },
        ];
      } else {
        updatedFilters =
          filters?.filter((filter) => filter.label !== label) || [];

        if (!isADRChecked(IAdr.YES) && !isADRChecked(IAdr.NO)) {
          updatedFilters = [];
        }
      }

      tableContextState?.updateFilters(updatedFilters);
    };

  const VesselFilter = () => {
    const hasVessel = (): string => {
      if (filters === null) return "";
      const filteredFilter = filters?.filter(
        (filter) => filter.column === FilterColumnTypes.VESSEL
      )[0];
      if (filteredFilter === undefined) return "";
      return filteredFilter.value ? (filteredFilter.value as string) : "";
    };
    const vesselHandleChange = (event: { target: { value: string } }) => {
      // setIsChecked(!isChecked);
      const str = event.target.value;
      const filterOption: IFilterOption = {
        column: FilterColumnTypes.VESSEL,
        label: str,
        name: "VESSEL",
        value: str,
      };
      const updatedFilters: IFilterOption[] = updateFilters(
        filters,
        filterOption
      );
      tableContextState?.updateFilters(updatedFilters);
    };
    return (
      <div className="checkboxes">
        <VesselSelect
          hasVessel={hasVessel()}
          onVesselChange={vesselHandleChange}
          onSelectOpen={setSelectOpen}
        />
      </div>
    );
  };
  const [selectedPOD, setSelectedPOD] = useState("");

  const handlePODChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value as string;
    setSelectedPOD(selectedValue);

    const filterOption: IFilterOption = {
      column: FilterColumnTypes.POD,
      label: selectedValue,
      name: "POD",
      value: selectedValue,
    };

    const updatedFilters: IFilterOption[] = updateFilters(
      filters,
      filterOption
    );
    tableContextState?.updateFilters(updatedFilters);
  };

  const handleCustomerName = (customerName: string, customerId: string) => {
    const filterOption: IFilterOption = {
      column: FilterColumnTypes.CUSTOMERNAME,
      label: customerName ?? "",
      name: "CUSTOMERNAME",
      value: customerId ?? "",
    };

    const updatedFilters: IFilterOption[] = updateFilters(
      filters,
      filterOption
    );
    tableContextState?.updateFilters(updatedFilters);
  };
  const isCustomerName = () => {
    if (filters === undefined || filters === null) return "";
    if (filters.length > 0) {
      const customerName = filters!.filter(
        (filter) => filter.name === "CUSTOMERNAME"
      )[0];
      if (customerName) {
        return customerName.label as string;
      } else {
        return "";
      }
    } else {
      return "";
    }
  };

  const PODFilter = () => (
    <div className="checkboxes">
      <div>
        <FormControl fullWidth>
          <Select
            labelId="pod-select-label"
            id="pod-select"
            value={selectedPOD}
            onOpen={() => setSelectOpen(true)}
            onClose={() => setSelectOpen(false)}
            onChange={(event) => handlePODChange(event)}
            displayEmpty
            inputProps={{ "aria-label": "Without label" }}
          >
            <MenuItem value="RAIL">RAIL</MenuItem>
            <MenuItem value="ROAD">ROAD</MenuItem>
          </Select>
        </FormControl>
      </div>
    </div>
  );

  const tooltipProps = {
    tooltip: {
      sx: {
        backgroundColor: customColors.groupBlue,
        color: customColors.white,
      },
    },
    arrow: {
      sx: {
        color: customColors.groupBlue,
      },
    },
  };

  const renderCheckboxFilters = () => {
    switch (filterColumnTypes) {
      case FilterColumnTypes.POD:
        return (
          <>
            {/* <Typography
              className={`filter__status ${classes.filterStatusSpacing}`}
              variant="subtitle1"
            >
              POD
            </Typography> */}
            <div className="filter__checkboxes-column">{PODFilter()}</div>
          </>
        );
      case FilterColumnTypes.STATUS:
        return (
          <>
            {/* <Typography
              className={`filter__status ${classes.filterStatusSpacing}`}
              variant="subtitle1"
            >
              Status
            </Typography> */}
            <div className="filter__checkboxes-column">
              <div className="checkboxes">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isStatusChecked(IStatus.NEW)}
                      color="primary"
                      onChange={statusHandleChange(IStatus.NEW)}
                      value={IStatus.NEW}
                    />
                  }
                  label="New"
                />
              </div>
              <div className="checkboxes">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isStatusChecked(IStatus.SENTTOSIMPLY)}
                      color="primary"
                      onChange={statusHandleChange(IStatus.SENTTOSIMPLY)}
                      value={IStatus.SENTTOSIMPLY}
                    />
                  }
                  label="Sent to simply"
                />
              </div>

              <div className="checkboxes">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isStatusChecked(IStatus.CREATEDINSIMPLY)}
                      color="primary"
                      onChange={statusHandleChange(IStatus.CREATEDINSIMPLY)}
                      value={IStatus.CREATEDINSIMPLY}
                    />
                  }
                  label="Created in simply"
                />
              </div>
            </div>
          </>
        );

      case FilterColumnTypes.VESSEL:
        return (
          <>
            {VesselFilter()}
            {/* <VesselSelect onSelectOpen={setSelectOpen} /> */}
          </>
        );

      case FilterColumnTypes.ADR:
        return (
          <>
            <div className="checkboxes">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isADRChecked(IAdr.YES)}
                    color="primary"
                    onChange={ADRhandleChange(IAdr.YES)}
                    value={IAdr.YES}
                  />
                }
                label="YES"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isADRChecked(IAdr.NO)}
                    color="primary"
                    onChange={ADRhandleChange(IAdr.NO)}
                    value={IAdr.NO}
                  />
                }
                label="NO"
              />
            </div>
          </>
        );

      case FilterColumnTypes.CUSTOMERNAME:
        return (
          <Tooltip
            title={
              <Typography sx={{ fontSize: "1rem" }}>
                {" "}
                Please enter at least 3 letters
              </Typography>
            }
            arrow
            componentsProps={tooltipProps}
          >
            <div>
              <AutocompleteTextbox
                onCustomerNameSelect={handleCustomerName}
                defaultValue={isCustomerName()}
              />
            </div>
          </Tooltip>
        );
      default:
        return null;
    }
  };

  return (
    <Box className={`filters ${classes.boxContainer}`}>
      <button
        aria-describedby={id}
        className={"filterButton"}
        onClick={(e) => handleClick(e)}
        // variant="contained"
      >
        <i className="material-icons">
          <span style={textColor}>filter_list</span>
        </i>
        <span className={`filters__button-message`} style={textColor}></span>
      </button>

      <Dialog open={open} style={overlayIndex}>
        <Popper
          style={{ zIndex: 2 }}
          placement="bottom-start"
          modifiers={[
            {
              name: "flip",
              enabled: false,
            },
            {
              name: "preventOverflow",
              enabled: false,
              options: {
                boundariesElement: "disabled",
              },
            },
          ]}
          anchorEl={anchorEl as HTMLElement}
          id={id}
          open={open}
          transition
        >
          {({ TransitionProps }) => (
            <ClickAwayListener
              onClickAway={handleClickAway}
              disableReactTree={true}
            >
              <Fade {...TransitionProps} timeout={350}>
                <Paper
                  style={{
                    transform:
                      filterColumnTypes === FilterColumnTypes.POD
                        ? "translateX(-50%)"
                        : "",
                  }}
                >
                  <div className={classes.popperDimensions}>
                    <Typography
                      className={`filter__title ${classes.filterTitleSpacing}`}
                    >
                      {filterColumnTypes.toString()} FILTER
                    </Typography>

                    <FormGroup className={classes.contentContainer}>
                      {renderCheckboxFilters()}
                    </FormGroup>
                  </div>
                </Paper>
              </Fade>
            </ClickAwayListener>
          )}
        </Popper>
      </Dialog>
    </Box>
  );
};

export default FilterOptions;
